// A list of audit entries: when, who, what, and an expandable before → after.
// A list (not a table) so it reads the same at 1440px and 390px.

import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import type { AuditEntry } from '@/api/types'
import { Badge, RelativeTime } from '@/components/ui'
import { ChevronRightIcon } from '@/components/ui/icons'
import { auditHeadline } from '@/lib/audit'
import { cn } from '@/lib/format'
import { ChangeDiff } from './ChangeDiff'

const ACTION_TONE = { create: 'good', update: 'neutral', delete: 'bad' } as const

export function AuditEntryList({ entries, showTenant = true, label }: { entries: AuditEntry[]; showTenant?: boolean; label: string }) {
  return (
    <ul aria-label={label} className="divide-y divide-line">
      {entries.map((e) => (
        <AuditEntryRow key={e.id} entry={e} showTenant={showTenant} />
      ))}
    </ul>
  )
}

function AuditEntryRow({ entry: e, showTenant }: { entry: AuditEntry; showTenant: boolean }) {
  const [open, setOpen] = useState(false)
  const detailId = useId()
  const tone = ACTION_TONE[e.action as keyof typeof ACTION_TONE] ?? 'outline'
  const changeCount = e.changes.length
  return (
    <li className="px-4 py-3 sm:px-5">
      <div className="grid gap-1.5 sm:grid-cols-[8.5rem_minmax(0,1fr)] sm:gap-x-4">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted sm:block">
          <RelativeTime value={e.created_at} className="block text-ink/90" />
          <Badge tone={tone} className="mt-0 sm:mt-1.5">
            {e.action}
          </Badge>
        </div>
        <div className="grid min-w-0 gap-1">
          <p className="break-words font-semibold text-ink">{auditHeadline(e)}</p>
          <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted">
            {e.actor ? (
              <>
                <span className="break-all">{e.actor.name || e.actor.email}</span>
                {e.actor.is_super_admin ? <Badge tone="accent">ScoreLM</Badge> : null}
              </>
            ) : (
              <span>System</span>
            )}
            {showTenant && e.league ? (
              <>
                <span aria-hidden className="text-subtle">
                  ·
                </span>
                <Link to={`/tenants/${e.league.id}/activity`} className="break-all text-muted underline-offset-2 hover:text-accent hover:underline">
                  {e.league.name}
                </Link>
              </>
            ) : null}
            {e.entity_type ? (
              <>
                <span aria-hidden className="text-subtle">
                  ·
                </span>
                <span className="font-mono">
                  {e.entity_type}
                  {e.entity_id != null ? ` #${e.entity_id}` : ''}
                </span>
              </>
            ) : null}
          </p>
          {changeCount || e.reason || e.old_values != null || e.new_values != null ? (
            <div>
              <button
                type="button"
                aria-expanded={open}
                aria-controls={detailId}
                onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center gap-1 rounded text-xs font-semibold text-accent hover:underline"
              >
                <ChevronRightIcon size={13} className={cn('transition-transform', open && 'rotate-90')} />
                {open ? 'Hide changes' : changeCount ? `Show ${changeCount} change${changeCount === 1 ? '' : 's'}` : 'Show details'}
              </button>
              {open ? (
                <div id={detailId} className="mt-2 rounded-md border border-line bg-raised/40 p-3">
                  <ChangeDiff entry={e} />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </li>
  )
}
